/**
 * 住所から位置（緯度・経度）を探す。打刻できる場所を登録するときに使う。
 *
 * 緯度・経度を数字で打ち込ませない（登録する人は数字を知らない）。住所を入れて候補から選ぶ。
 * 国土地理院の公開の仕組みを使う。送るのは入力した住所の文字だけ（アプリの認証の情報は付けない）。
 *
 * **必ず答えを返す（失敗を投げない）。**探せなければ空の一覧。
 */

const ENDPOINT = 'https://msearch.gsi.go.jp/address-search/AddressSearch'
const TIMEOUT_MS = 8000
const MAX_RESULTS = 10

/** 応答（GeoJSON の Feature の並び）から、候補の一覧を作る。 */
export const placeCandidatesFrom = body => {
  if (!Array.isArray(body)) return []
  const seen = new Set()
  const candidates = []
  body.forEach(feature => {
    const coordinates = feature && feature.geometry && feature.geometry.coordinates
    const title = feature && feature.properties && feature.properties.title
    if (!Array.isArray(coordinates) || coordinates.length < 2 || typeof title !== 'string') return
    const longitude = Number(coordinates[0])
    const latitude = Number(coordinates[1])
    if (!isFinite(latitude) || !isFinite(longitude)) return
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return
    const key = title + '|' + latitude.toFixed(6) + '|' + longitude.toFixed(6)
    if (seen.has(key) || candidates.length >= MAX_RESULTS) return
    seen.add(key)
    candidates.push({ title: title.trim(), latitude, longitude })
  })
  return candidates
}

/**
 * @param {string} address 探す住所
 * @param {object} [options] 試験用（fetchImpl）
 * @returns {Promise<Array<{title: string, latitude: number, longitude: number}>>}
 */
export const searchPlacesByAddress = (address, options = {}) => {
  const query = address == null ? '' : String(address).trim()
  if (!query) return Promise.resolve([])
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null)
  if (!fetchImpl) return Promise.resolve([])

  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), TIMEOUT_MS) : null
  return Promise.resolve()
    .then(() => fetchImpl(ENDPOINT + '?q=' + encodeURIComponent(query), {
      method: 'GET',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: controller ? controller.signal : undefined
    }))
    .then(response => (response && response.ok ? response.json() : null))
    .then(placeCandidatesFrom)
    .catch(() => [])
    .then(candidates => {
      if (timer) clearTimeout(timer)
      return candidates
    })
}
